import React, { useState } from 'react';
import Icon from './ui-stubs';
import { format, differenceInDays, addDays } from 'date-fns';

const TimelineView = ({ tasks, onTaskClick }) => {
  const [zoomLevel, setZoomLevel] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

  // Calculate timeline range based on zoom level
  const getTimelineRange = () => {
    const start = new Date(currentDate);
    let days = 30;
    if (zoomLevel === 'day') days = 7;
    if (zoomLevel === 'week') days = 30;
    if (zoomLevel === 'month') days = 90;
    return { start, days };
  };

  const { start: timelineStart, days: timelineDays } = getTimelineRange();

  // Generate date columns
  const getDateColumns = () => {
    const columns = [];
    for (let i = 0; i < timelineDays; i++) {
      columns?.push(addDays(timelineStart, i));
    }
    return columns;
  };

  const dateColumns = getDateColumns();

  // Calculate task bar position and width
  const getTaskBarStyle = (task) => {
    const taskStart = new Date(task?.startDate);
    const taskEnd = new Date(task?.dueDate);
    const timelineStartDate = timelineStart;

    const startOffset = differenceInDays(taskStart, timelineStartDate);
    const duration = differenceInDays(taskEnd, taskStart) + 1;

    const columnWidth = 100 / timelineDays;
    const left = startOffset * columnWidth;
    const width = duration * columnWidth;

    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.min(width, 100 - left)}%`,
    };
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-500';
      case 'High':
        return 'bg-orange-500';
      case 'Medium':
        return 'bg-yellow-500';
      case 'Low':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleZoomIn = () => {
    if (zoomLevel === 'month') setZoomLevel('week');
    if (zoomLevel === 'week') setZoomLevel('day');
  };

  const handleZoomOut = () => {
    if (zoomLevel === 'day') setZoomLevel('week');
    if (zoomLevel === 'week') setZoomLevel('month');
  };

  const handlePrevious = () => {
    const daysToMove = zoomLevel === 'day' ? 7 : zoomLevel === 'week' ? 30 : 90;
    setCurrentDate(new Date(currentDate?.getTime() - daysToMove * 24 * 60 * 60 * 1000));
  };

  const handleNext = () => {
    const daysToMove = zoomLevel === 'day' ? 7 : zoomLevel === 'week' ? 30 : 90;
    setCurrentDate(new Date(currentDate?.getTime() + daysToMove * 24 * 60 * 60 * 1000));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      {/* Timeline Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-bold text-gray-900">Timeline</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevious}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Icon name="ChevronLeft" size={20} className="text-gray-600" />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Today
            </button>
            <button
              onClick={handleNext}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Icon name="ChevronRight" size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleZoomOut}
            disabled={zoomLevel === 'month'}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Zoom Out"
          >
            <Icon name="ZoomOut" size={20} className="text-gray-600" />
          </button>
          <span className="text-sm font-medium text-gray-700 capitalize">{zoomLevel}</span>
          <button
            onClick={handleZoomIn}
            disabled={zoomLevel === 'day'}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Zoom In"
          >
            <Icon name="ZoomIn" size={20} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Timeline Grid */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <div className="min-w-[1200px]">
          {/* Date Headers */}
          <div className="flex border-b border-border bg-gray-50">
            <div className="w-64 flex-shrink-0 px-4 py-3 border-r border-border">
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Task
              </span>
            </div>
            <div className="flex-1 flex">
              {dateColumns?.map((date, index) => {
                const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                return (
                  <div
                    key={index}
                    className={`flex-1 px-2 py-3 text-center border-r border-border ${
                      isToday ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="text-xs font-medium text-gray-700">
                      {zoomLevel === 'day' && format(date, 'EEE')}
                      {zoomLevel === 'week' && format(date, 'MMM d')}
                      {zoomLevel === 'month' && format(date, 'MMM d')}
                    </div>
                    {zoomLevel === 'day' && (
                      <div className={`text-xs ${
                        isToday ? 'text-blue-600 font-semibold' : 'text-gray-500'
                      }`}>
                        {format(date, 'd')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Task Rows */}
          <div className="divide-y divide-gray-200">
            {tasks?.map((task) => {
              const barStyle = getTaskBarStyle(task);
              return (
                <div key={task?.id} className="flex hover:bg-gray-50 transition-colors">
                  {/* Task Info */}
                  <div className="w-64 flex-shrink-0 px-4 py-4 border-r border-border">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {task?.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{task?.id}</div>
                    <div className="flex items-center space-x-1 mt-2">
                      {task?.assignees?.slice(0, 3)?.map((assignee) => (
                        <div
                          key={assignee?.id}
                          className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold"
                          title={assignee?.name}
                        >
                          {assignee?.avatar?.charAt(0)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Timeline Bar */}
                  <div className="flex-1 relative py-4">
                    <div className="relative h-8">
                      <button
                        onClick={() => onTaskClick(task)}
                        className={`absolute h-full rounded-md shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                          getPriorityColor(task?.priority)
                        } text-white px-3 flex items-center justify-between`}
                        style={barStyle}
                      >
                        <span className="text-xs font-medium truncate">{task?.title}</span>
                        {task?.dependencies?.length > 0 && (
                          <Icon name="Link" size={12} className="ml-2" />
                        )}
                      </button>
                    </div>

                    {/* Dependency Lines (simplified visualization) */}
                    {task?.dependencies?.length > 0 && (
                      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                        <svg className="w-full h-full">
                          <line
                            x1="0"
                            y1="16"
                            x2="20"
                            y2="16"
                            stroke="#9CA3AF"
                            strokeWidth="2"
                            strokeDasharray="4"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-end space-x-4 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-red-500 rounded"></div>
          <span className="text-gray-600">Critical</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-orange-500 rounded"></div>
          <span className="text-gray-600">High</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-yellow-500 rounded"></div>
          <span className="text-gray-600">Medium</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span className="text-gray-600">Low</span>
        </div>
        <div className="flex items-center space-x-1">
          <Icon name="Link" size={12} className="text-gray-600" />
          <span className="text-gray-600">Has Dependencies</span>
        </div>
      </div>
    </div>
  );
};

export default TimelineView;